import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { formatDateWithTime } from '@shared/utils/FormatUtils';
import { resolveApiAssetUrl } from '@shared/utils/resolveApiAssetUrl';
import { faArrowRight, faCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShipmentStatusCorrection } from '../../adapters/OrdersApiAdapter';
import { useShipmentStatusCorrections } from '../../hooks/useShipmentStatusCorrections';

export const ShipmentStatusCorrectionsModal = forwardRef<ModalCenterRef>((_, ref) => {
  const { t } = useTranslation('viewOrdersModule');
  const { data: corrections, isLoading, error } = useShipmentStatusCorrections();
  const previewModalRef = useRef<ModalCenterRef>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const openImagePreview = (url: string) => {
    setPreviewImage(resolveApiAssetUrl(url));
    previewModalRef.current?.openModal();
  };

  const getAttachmentName = (url: string, index: number) => {
    const segments = url.split('/');
    return segments[segments.length - 1] || t('shipmentStatusCorrections.attachmentFallback', { index: index + 1 });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const renderContent = (correction: ShipmentStatusCorrection, index: number, total: number) => {
    const isLast = index === total - 1;
    const useTimeline = total > 1;
    const trackingDateChanged =
      correction.before_snapshot.date_tracking_update !== correction.after_snapshot.date_tracking_update;

    const body = (
      <>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-bg-info text-info">
            {t('shipmentStatusCorrections.type.correction')}
          </span>
          <span className="text-xs text-complementary-text shrink-0">{formatDate(correction.created_at)}</span>
        </div>
        {correction.user_name && (
          <p className="text-xs text-complementary-text mt-1">
            <span className="font-medium">{t('shipmentStatusCorrections.operator')}: </span>
            <span className="text-text">{correction.user_name}</span>
          </p>
        )}
        <div className="flex items-center gap-1.5 text-sm mt-1">
          <span className="text-complementary-text">{correction.before_snapshot.shipment_status_name}</span>
          <FontAwesomeIcon icon={faArrowRight} className="!w-3 !h-3 text-complementary-text" />
          <span className="font-medium">{correction.after_snapshot.shipment_status_name}</span>
        </div>
        {trackingDateChanged && (
          <div className="flex items-center gap-1.5 text-xs text-complementary-text mt-0.5">
            <span>
              {correction.before_snapshot.date_tracking_update
                ? formatDateWithTime(correction.before_snapshot.date_tracking_update)
                : '—'}
            </span>
            <FontAwesomeIcon icon={faArrowRight} className="!w-2.5 !h-2.5" />
            <span className="text-text font-medium">
              {correction.after_snapshot.date_tracking_update
                ? formatDateWithTime(correction.after_snapshot.date_tracking_update)
                : '—'}
            </span>
          </div>
        )}
        <p className="text-xs text-complementary-text mt-1">
          <span className="font-medium">{t('shipmentStatusCorrections.reason')}: </span>
          <span className="text-text">{correction.reason}</span>
        </p>
        {correction.attachments?.length > 0 && (
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {correction.attachments.map((url, attachmentIndex) => {
              const resolvedUrl = resolveApiAssetUrl(url);
              if (!resolvedUrl) return null;

              return (
                <button
                  key={`${correction.id}-${attachmentIndex}`}
                  type="button"
                  className="overflow-hidden rounded-md border border-card-border hover:bg-hover-press"
                  onClick={() => openImagePreview(url)}
                  data-testid={`shipment-status-correction-attachment-${correction.id}-${attachmentIndex}`}
                >
                  <img
                    src={resolvedUrl}
                    alt={getAttachmentName(url, attachmentIndex)}
                    className="h-20 w-full object-cover"
                  />
                </button>
              );
            })}
          </div>
        )}
        {useTimeline && !isLast && <div className="mt-3 mb-3 border-t border-card-border" />}
      </>
    );

    if (!useTimeline) {
      return <div key={correction.id}>{body}</div>;
    }

    return (
      <div
        key={correction.id}
        className={`flex items-start gap-4 relative !mt-0 ${isLast ? 'bg-card-bg' : 'bg-transparent'}`}
      >
        <div className="relative z-10 flex items-start border-[5px] border-card-bg rounded-full ml-[-2px]">
          <FontAwesomeIcon icon={faCircle} className="!w-3 !h-3 text-subtext bg-card-bg" />
        </div>
        <div className="flex-1 min-w-0 pb-1">{body}</div>
      </div>
    );
  };

  const list = corrections ?? [];
  const useTimeline = list.length > 1;

  return (
    <>
      <ModalCenter
        ref={ref}
        title={t('shipmentStatusCorrections.title')}
        width="lg"
        type="default"
        fullScreenMobile={true}
        data-testid="shipment-status-corrections-modal"
      >
        <div className="max-h-[70vh] overflow-y-auto py-2">
          {isLoading && <p className="text-sm text-complementary-text">{t('shipmentStatusCorrections.loading')}</p>}
          {!isLoading && error && <p className="text-sm text-subtext">{t('shipmentStatusCorrections.error')}</p>}
          {!isLoading && !error && list.length === 0 && (
            <p className="text-sm text-subtext">{t('shipmentStatusCorrections.empty')}</p>
          )}
          {!isLoading &&
            !error &&
            list.length > 0 &&
            (useTimeline ? (
              <div className="relative">
                <div className="absolute left-2 top-1 bottom-0 w-px bg-lines" />
                <div className="space-y-4">{list.map((c, i) => renderContent(c, i, list.length))}</div>
              </div>
            ) : (
              <div>{list.map((c, i) => renderContent(c, i, list.length))}</div>
            ))}
        </div>
      </ModalCenter>

      <ModalCenter ref={previewModalRef} title={t('shipmentStatusCorrections.imagePreview')} width="lg" type="default">
        {previewImage && (
          <div className="flex items-center justify-center p-2">
            <img src={previewImage} alt="" className="max-h-[75vh] max-w-full rounded-md object-contain" />
          </div>
        )}
      </ModalCenter>
    </>
  );
});

ShipmentStatusCorrectionsModal.displayName = 'ShipmentStatusCorrectionsModal';
